import type { ControllerPermission } from '@/shared/store/atoms/sessionAtom';

/**
 * IDs dos controllers do backend conforme retornado por `/api/v1/access/controllers/all`.
 *
 * Use este enum em vez de números mágicos ao referenciar controllers.
 */
export enum EController {
  Access = 7,
  Exam = 9,
  Document = 8,
  Person = 11,
  ImagemLogin = 12,
  CourseRegistration = 13,
  Location = 14,
  ClassPlanning = 15,
  User = 16,
  Notification = 17,
  IndividualPayment = 18,
  Banner = 20,
  TutorAI = 29,
  Serie = 31,
}

/**
 * Configuração de um controller no sistema de permissões.
 *
 * Fonte única de verdade que amarra o `EController`, o nome usado no segmento
 * da URL de API (o `controllerName` retornado no login) e os prefixos de rota
 * frontend que exigem aquele controller. Todos os mapas e conjuntos deste
 * módulo são derivados desta lista — não mantenha listas paralelas.
 */
interface ControllerConfig {
  /** ID do controller (enum). */
  readonly id: EController;
  /**
   * Nome do controller como retornado no login (`controllerName`) e usado no
   * segmento da URL de API (ex: `person` em `/api/v1/person/123`).
   */
  readonly apiName: string;
  /**
   * Prefixos de rota frontend que exigem este controller. Vazio quando o
   * controller só protege chamadas de API e não tem tela associada.
   */
  readonly routePrefixes: readonly string[];
}

/**
 * Fonte única de verdade do sistema de permissões por controller.
 *
 * Ao adicionar um novo controller/rota protegida, inclua uma entrada aqui —
 * o guard de rota, o filtro de sidebar e o guard de API passam a respeitá-la
 * automaticamente.
 */
const CONTROLLER_CONFIG: readonly ControllerConfig[] = [
  { id: EController.Access, apiName: 'access', routePrefixes: ['/access-management'] },
  {
    id: EController.Exam,
    apiName: 'exam',
    routePrefixes: ['/exams', '/exam', '/question-library'],
  },
  { id: EController.Document, apiName: 'document', routePrefixes: [] },
  { id: EController.Person, apiName: 'person', routePrefixes: ['/persons'] },
  { id: EController.ImagemLogin, apiName: 'imagemlogin', routePrefixes: [] },
  {
    id: EController.CourseRegistration,
    apiName: 'courseregistration',
    routePrefixes: ['/enrollments'],
  },
  { id: EController.Location, apiName: 'location', routePrefixes: ['/location'] },
  {
    id: EController.ClassPlanning,
    apiName: 'classplanning',
    routePrefixes: ['/slide-editor', '/slide-library', '/class-modules', '/class-planning'],
  },
  { id: EController.User, apiName: 'user', routePrefixes: ['/users'] },
  { id: EController.Notification, apiName: 'notification', routePrefixes: ['/notification'] },
  {
    id: EController.IndividualPayment,
    apiName: 'individualpayment',
    routePrefixes: ['/individual-payment'],
  },
  { id: EController.Banner, apiName: 'banner', routePrefixes: ['/banners'] },
  { id: EController.TutorAI, apiName: 'tutorai', routePrefixes: ['/tutor-ai'] },
  { id: EController.Serie, apiName: 'serie', routePrefixes: ['/series'] },
];

/**
 * Rotas autenticadas liberadas a qualquer usuário logado, sem exigir controller.
 *
 * Diferente de uma rota não mapeada (que é negada por padrão — fail-closed),
 * estas são explicitamente públicas dentro da área autenticada.
 */
const PUBLIC_AUTHENTICATED_PREFIXES = ['/', '/unauthorized'] as const;

/**
 * Conjunto de segmentos de controller de API gerenciados pelo sistema de
 * permissões (derivado de {@link CONTROLLER_CONFIG}). Requisições para esses
 * controllers sem a devida permissão são bloqueadas no interceptor do Axios.
 *
 * O guard de API é um denylist: controllers fora desta lista (ex: `accesscontrol`,
 * `materials`) passam livremente, pois não fazem parte do sistema de permissões.
 */
const GUARDED_API_CONTROLLERS = new Set(CONTROLLER_CONFIG.map(c => c.apiName));

/**
 * Verifica se o pathname casa com um prefixo de rota.
 *
 * O prefixo `/` (home) casa apenas com a rota exata; os demais casam com a
 * rota exata ou qualquer subrota.
 */
function matchesPrefix(pathname: string, prefix: string): boolean {
  if (prefix === '/') return pathname === '/';
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Indica se o pathname é uma rota autenticada pública (sem exigência de controller).
 *
 * @param pathname - Pathname da rota (ex: `/`)
 */
export function isPublicAuthenticatedRoute(pathname: string): boolean {
  return PUBLIC_AUTHENTICATED_PREFIXES.some(prefix => matchesPrefix(pathname, prefix));
}

/**
 * Retorna os controllers exigidos para acessar o pathname informado.
 *
 * Retorna `[]` quando nenhum controller mapeia o pathname — o que inclui tanto
 * rotas públicas quanto rotas desconhecidas. Para decidir acesso, use
 * {@link isRouteAuthorized}, que distingue os dois casos.
 *
 * @param pathname - Pathname da rota (ex: `/persons`)
 */
export function getRequiredControllerIds(pathname: string): EController[] {
  return CONTROLLER_CONFIG.filter(c =>
    c.routePrefixes.some(prefix => matchesPrefix(pathname, prefix)),
  ).map(c => c.id);
}

/**
 * Verifica se o usuário tem permissão para acessar o pathname informado.
 *
 * Comporta-se como fail-closed: rotas públicas ({@link isPublicAuthenticatedRoute})
 * são sempre liberadas; rotas mapeadas exigem todos os controllers correspondentes;
 * qualquer outra rota (não mapeada) é **negada** por padrão.
 *
 * @param pathname - Pathname da rota (ex: `/persons`)
 * @param permissions - Permissões do usuário (de `SessionData.controllerPermissions`)
 */
export function isRouteAuthorized(pathname: string, permissions: ControllerPermission[]): boolean {
  if (isPublicAuthenticatedRoute(pathname)) return true;

  const required = getRequiredControllerIds(pathname);
  // Fail-closed: rota não mapeada e não-pública é negada por padrão.
  if (required.length === 0) return false;

  return required.every((id: EController) => permissions.some(p => p.controllerId === id));
}

/**
 * Verifica se o usuário tem permissão para realizar uma chamada de API
 * com base na URL da requisição.
 *
 * Apenas controllers listados em {@link GUARDED_API_CONTROLLERS} são verificados.
 * Requisições para controllers desconhecidos ou não protegidos passam livremente.
 *
 * @param url - URL da requisição (ex: `/api/v1/exam/123`)
 * @param permissions - Permissões do usuário (de `SessionData.controllerPermissions`)
 */
export function isApiControllerAuthorized(
  url: string,
  permissions: ControllerPermission[],
): boolean {
  const match = url.match(/\/api\/v1\/([^/?]+)/i);
  if (!match) return true;

  const segment = match?.[1]?.toLowerCase() ?? '';
  if (!GUARDED_API_CONTROLLERS.has(segment)) return true;

  return permissions.some(p => p.controllerName.toLowerCase() === segment);
}
